import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useUpdateSalesSettingsPermissions } from 'src/components/Sales/BulkActions/useUpdateSalesSettingsPermissions';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkEditSalesSettingsDialog } from 'src/dialogs/BulkEdits/BulkEditSalesSettingsDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { lookupEventInCatalog } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  CommandOptionType,
  Event,
  SaleInput,
  SaleQuery,
} from 'src/WebApiController';

import { SALES_BULK_UPDATE_GENERAL_SETTINGS_UPDATE_KEY } from '../../SalesActionDropdown.constants';
import { LaunchUpdateSalesSettings } from './LaunchUpdateSaleSettings';

export const useBulkUpdateSalesSettings = (
  filterQueryWithEventIds: SaleQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasUpdateSalesSettingsPermission } =
    useUpdateSalesSettingsPermissions();

  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `salesSettings-event-${event?.virtId}`
    : SALES_BULK_UPDATE_GENERAL_SETTINGS_UPDATE_KEY;

  const {
    data,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkEditSalesSettingsDialog,
    closeDialog: closeBulkEditSalesSettingsDialog,
  } = useDialog(DialogId.BulkEditSalesSettings, BulkEditSalesSettingsDialog);

  const onSubmit = useCallback(
    async (
      saleInput: SaleInput | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkUpdateGeneralSettingsPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateGeneralSettingsPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (saleInput) {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkUpdateGeneralSettings(
              {
                item1: preview!.preview,
                item2: saleInput,
              },
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                await refreshExpandedListItems();
              }

              closeBulkEditSalesSettingsDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateGeneralSettings', error, {
              trackErrorData: { event, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkEditSalesSettingsDialog,
      event,
      filterQueryWithEventIds,
      refreshExpandedListItems,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkEditSalesSettingsDialog({
      event,
      sales: event
        ? lookupEventInCatalog(data, event.virtId)
            ?.entities?.sales?.filter((s) => s != null)
            ?.map((s) => s!)
        : [],
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditSalesSettingsDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkEditGeneralSettings,
        onSelect: openDialogStableRef,
      },
      disabled: !hasUpdateSalesSettingsPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkEditGeneralSettings,
        disabled: !hasUpdateSalesSettingsPermission,
      });
    };
  }, [
    hasUpdateSalesSettingsPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkEditSalesSettingsDialog({
        event,
        sales: event
          ? lookupEventInCatalog(data, event.virtId)
              ?.entities?.sales?.filter((s) => s != null)
              ?.map((s) => s!)
          : [],
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditSalesSettingsDialog,
      });
    },
    [
      closeBulkEditSalesSettingsDialog,
      data,
      event,
      onSubmit,
      openBulkEditSalesSettingsDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasUpdateSalesSettingsPermission) {
      return [];
    }

    return [
      {
        title: ContentId.EditGeneralSettings,
        onClick: onClick,
        disabled: isLoading,
      },
    ];
  }, [hasUpdateSalesSettingsPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchUpdateSalesSettings
        key="salesGeneralSettingsUpdate"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
